import { RefObject, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import {
  Filters,
  formatSearchParams,
  Pagination,
  PermissionEnum,
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useGetCreateCorporateCustomerValidatorQuery,
  useLazyGetCorporateUsersQuery,
} from '../../store/services/corporate';
import {
  corporateCustomersListFiltersSchema,
  corporateCustomersListSchema,
} from '../variables/list';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../../../store/dictionaries/services';
import formatCorporateCustomersList from '../helpers/formate-corporate-customer-list';
import { clearOverviewState } from '../../store/slices/overview';
import { clearAmlState } from '../../../common/store/slices/aml';
import { clearStatisticState } from '../../../common/store/slices/statistics';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../../../common/store/slices/statements';
import { clearDocumentsState } from '../../../common/store/slices/documents/documents';
import { clearTariffsState } from '../../../common/store/slices/tariff/tariff';
import { clearExternalAccounts } from '../../../common/store/slices/external-integration';
import { clearGeneralState } from '../../store/slices/general-info';
import { clearTopUpOptions } from '../../../common/store/slices/top-up-panel';
import { CorporateGeneralInfo, CorporateOverviewGeneral } from '../../types';
import { clearCorporateList } from '../../store/slices/list';
import { CustomerProps, ApplicationScheme } from '../../../common/types';

let timeout: NodeJS.Timeout;

const useList = ({
  pagination,
  countries,
  corporateCustomers,
  incorporationDates,
  scrollRef,
  generalInformation,
  baseInformation,
  baseInfo,
  applicationSchemes,
  activePermissions,
}: {
  pagination?: Pagination;
  countries?: SelectItemProps[];
  corporateCustomers?: CustomerProps[];
  incorporationDates?: SelectItemProps[];
  scrollRef: RefObject<HTMLDivElement | null>;
  generalInformation?: CorporateGeneralInfo;
  baseInformation?: Partial<CorporateGeneralInfo>;
  baseInfo?: Partial<CorporateOverviewGeneral>;
  applicationSchemes?: ApplicationScheme[];
  activePermissions?: PermissionEnum[];
}) => {
  const [countriesList, setCountriesList] = useState<
    SelectItemProps[] | undefined
  >(undefined);
  const [companyNameSearch, setCompanyNameSearch] = useState('');

  useGetCreateCorporateCustomerValidatorQuery();

  const [getCorporateList, { isFetching, isLoading }] =
    useLazyGetCorporateUsersQuery();
  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const prevPagination = usePrevious(pagination);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    corporateCustomersListSchema,
  );

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<CustomerProps>({
      getList: getCorporateList,
      onFilterChange,
      onSortChange,
      schema: corporateCustomersListSchema,
    });

  const prevFilters = usePrevious(filters);

  const onSearchAutocomplete = (name: string, value: string) => {
    const nextCountries = countries?.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase()),
    );

    setCountriesList(nextCountries);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: corporateCustomersListFiltersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const formattedData = useMemo(() => {
    const data = formatCorporateCustomersList(
      corporateCustomers as CustomerProps[],
      countries,
      incorporationDates,
    );

    return data;
  }, [corporateCustomers, filters, sort, countries]);

  const onClear = () => {
    dispatch(clearOverviewState());
    dispatch(clearAmlState());
    dispatch(clearStatisticState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearExternalAccounts());
  };

  const onTableFilterChange = (values: Filters) => {
    const nextValues = { ...values };

    if (companyNameSearch) {
      nextValues.companyName = {
        value: [companyNameSearch],
        type: SchemaFilterType.STRING,
      };
    }

    if (!nextValues.status) {
      nextValues.status = {
        value: ['ACTIVE', 'CLOSED'],
        type: SchemaFilterType.STRING,
      };
    }

    onFilter(nextValues);

    localStorage.setItem(
      'corporateOrganizationsFilters',
      JSON.stringify({ ...nextValues }),
    );
  };

  const onTableSetPage = (data: { id: string; take: string }) => {
    const nextFilters: Filters = { ...filters };

    if (companyNameSearch) {
      nextFilters.companyName = {
        value: [companyNameSearch],
        type: SchemaFilterType.STRING,
      };
    }

    setPage({ ...data, filters: nextFilters });

    localStorage.setItem(
      'corporateOrganizationsFilters',
      JSON.stringify({
        ...filters,
        cursor: { value: [data.id], type: SchemaFilterType.STRING },
      }),
    );
  };

  const onCompanySearch = (searchText: string) => {
    clearTimeout(timeout);

    setCompanyNameSearch(searchText);

    const nextFilters: Filters = { ...filters };

    if (searchText) {
      nextFilters.companyName = {
        value: [searchText],
        type: SchemaFilterType.STRING,
      };
    } else if (!searchText && nextFilters.companyName) {
      delete nextFilters.companyName;
    }

    timeout = setTimeout(() => {
      onFilter({ ...nextFilters });
    }, 500);
  };

  useScroll({ element: scrollRef, requestInProgress: isFetching });

  useEffect(() => {
    if (!countriesList && countries) {
      setCountriesList(countries);
    }
  }, [countries, countriesList]);

  useEffect(() => {
    if (countriesList) {
      setValues('countryOfIncorporationId', countriesList);
    }
  }, [countriesList]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !corporateCustomers
    ) {
      let currentFilters = filters;
      const currentFiltersStorage = localStorage.getItem(
        'corporateOrganizationsFilters',
      );

      if (currentFiltersStorage) {
        currentFilters = JSON.parse(currentFiltersStorage);

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.companyName) {
            setCompanyNameSearch(parsed.companyName as string);

            currentFilters.companyName = {
              value: [parsed.companyName as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilterChange(currentFilters);
      }

      if (!filters.status && !currentFiltersStorage) {
        const filtersValues: Filters = { ...currentFilters };

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.companyName) {
            setCompanyNameSearch(parsed.companyName as string);

            // eslint-disable-next-line react-hooks/immutability
            currentFilters.companyName = {
              value: [parsed.companyName as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!filters.status) {
          filtersValues.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilter({ ...filtersValues });
      } else if (!corporateCustomers) {
        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.companyName) {
            setCompanyNameSearch(parsed.companyName as string);

            currentFilters.companyName = {
              value: [parsed.companyName as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    }
  }, [filters, prevFilters, corporateCustomers]);

  useEffect(() => {
    if (!corporateCustomers && search) {
      formatSearchParams({
        currentSchemaValues: corporateCustomersListSchema,
        storageKey: 'corporateOrganizationsFilters',
        search,
      });
    }
  }, [corporateCustomers, search]);

  useEffect(() => {
    const currentFilters = localStorage.getItem(
      'corporateOrganizationsFilters',
    );

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'corporateOrganizationsFilters',
          JSON.stringify({ ...parsed }),
        );
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    if (generalInformation || baseInformation) {
      dispatch(clearGeneralState());
      dispatch(clearTopUpOptions());
    }

    if (baseInfo) {
      onClear();
    }
  }, [generalInformation, baseInfo, baseInformation]);

  useEffect(() => {
    if (
      !applicationSchemes &&
      activePermissions?.includes(PermissionEnum.APPLICATIONS_SCHEMES_LIST)
    ) {
      getCustomersApplicationSchemes();
    }
  }, [applicationSchemes, activePermissions]);

  useEffect(() => {
    dispatch(clearCorporateList());
  }, []);

  return {
    isLoading,
    countriesList,
    formattedData,
    onSort,
    filters,
    sort,
    companyNameSearch,
    filtersComponent,
    handleSubmit,
    isFetching,
    onTableFilterChange,
    onTableSetPage,
    search,
    errorList,
    onCompanySearch,
  };
};

export default useList;
